import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Select, Button, Table, Tabs, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import api from '../../app/api';
import { AdminLayout } from '../../components/AdminLayout';
import { useTranslation } from '../../shared/hooks/useTranslation';
import style from './StatisticsDashboard.module.scss';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

interface StatisticsSummary {
  total_iin_attempts: number;
  successful_auths: number;
  total_appointments: number;
  unique_users: number;
  popular_services: Array<{
    service_id: string;
    service_name: string;
    count: number;
    language: string;
  }>;
  language_distribution: Array<{
    language: string;
    count: number;
    percent: number;
  }>;
  daily_activity: Array<{
    date: string;
    iin_attempts: number;
    auths: number;
    appointments: number;
  }>;
}

interface DailyStatistics {
  date: string;
  event_type: string;
  count: number;
  unique_users: number;
}

interface HourlyStatistics {
  date: string;
  hour: number;
  event_type: string;
  count: number;
  unique_users: number;
}

export function StatisticsDashboard() {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<StatisticsSummary | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStatistics[]>([]);
  const [hourlyStats, setHourlyStats] = useState<HourlyStatistics[]>([]);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const { t } = useTranslation();

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('start_date', dateRange[0]);
        params.append('end_date', dateRange[1]);
      }

      const response = await api.get(`/admin/statistics/summary?${params}`);
      setSummary(response.data);
    } catch (error) {
      message.error(t.statsErrorLoad);
    } finally {
      setLoading(false);
    }
  }, [dateRange, t.statsErrorLoad]);

  const loadDailyStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('start_date', dateRange[0]);
        params.append('end_date', dateRange[1]);
      }
      if (selectedEventType) {
        params.append('event_type', selectedEventType);
      }

      const response = await api.get(`/admin/statistics/daily?${params}`);
      setDailyStats(response.data);
    } catch (error) {
      message.error(t.statsErrorLoadDaily);
    }
  }, [dateRange, selectedEventType, t.statsErrorLoadDaily]);

  useEffect(() => {
    loadSummary();
    loadDailyStats();
  }, [loadSummary, loadDailyStats]);

  const loadHourlyStats = async (date: string) => {
    try {
      const params = new URLSearchParams();
      params.append('date', date);
      if (selectedEventType) {
        params.append('event_type', selectedEventType);
      }

      const response = await api.get(`/admin/statistics/hourly?${params}`);
      setHourlyStats(response.data);
    } catch (error) {
      message.error(t.statsErrorLoadHourly);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      params.append('format', 'csv');
      if (dateRange) {
        params.append('start_date', dateRange[0]);
        params.append('end_date', dateRange[1]);
      }

      const response = await api.get(`/admin/statistics/export?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'statistics.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(t.statsExportSuccess);
    } catch (error) {
      message.error(t.statsExportError);
    }
  };

  const dailyColumns = useMemo(() => [
    { title: t.statsColDate, dataIndex: 'date', key: 'date' },
    { title: t.statsColEventType, dataIndex: 'event_type', key: 'event_type' },
    { title: t.statsColCount, dataIndex: 'count', key: 'count' },
    { title: t.statsColUnique, dataIndex: 'unique_users', key: 'unique_users' },
  ], [t.statsColDate, t.statsColEventType, t.statsColCount, t.statsColUnique]);

  const hourlyColumns = useMemo(() => [
    { title: t.statsColHour, dataIndex: 'hour', key: 'hour' },
    { title: t.statsColEventType, dataIndex: 'event_type', key: 'event_type' },
    { title: t.statsColCount, dataIndex: 'count', key: 'count' },
    { title: t.statsColUnique, dataIndex: 'unique_users', key: 'unique_users' },
  ], [t.statsColHour, t.statsColEventType, t.statsColCount, t.statsColUnique]);

  return (
    <AdminLayout>
      <div className={style.container}>
        <div className={style.header}>
          <h1>{t.statsTitle}</h1>
          <div className={style.controls}>
            <RangePicker
              onChange={(dates) => {
                if (dates) {
                  setDateRange([
                    dates[0]?.format('YYYY-MM-DD') || '',
                    dates[1]?.format('YYYY-MM-DD') || ''
                  ]);
                } else {
                  setDateRange(null);
                }
              }}
            />
            <Select
              placeholder={t.statsEventFilter}
              style={{ width: 200 }}
              allowClear
              onChange={setSelectedEventType}
            >
              <Option value="iin_attempt">{t.statsEventIin}</Option>
              <Option value="successful_auth">{t.statsEventAuthSuccess}</Option>
              <Option value="appointment_created">{t.statsEventApptCreated}</Option>
              <Option value="service_selected">{t.statsEventServices}</Option>
              <Option value="doctor_selected">{t.statsEventDoctors}</Option>
            </Select>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              type="primary"
            >
              {t.statsExportButton}
            </Button>
          </div>
        </div>

      {summary && (
        <Row gutter={[16, 16]} className={style.summaryCards}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title={t.statsCardIinAttempts} value={summary.total_iin_attempts} loading={loading} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title={t.statsCardAuthSuccess} value={summary.successful_auths} loading={loading} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title={t.statsCardApptCreated} value={summary.total_appointments} loading={loading} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic title={t.statsCardUnique} value={summary.unique_users} loading={loading} />
            </Card>
          </Col>
        </Row>
      )}

      <Tabs defaultActiveKey="daily" className={style.tabs}>
        <TabPane tab={t.statsTabDaily} key="daily">
          <Card title={t.statsDailyTitle}>
            <Table
              columns={dailyColumns}
              dataSource={dailyStats}
              rowKey={(record) => `${record.date}-${record.event_type}`}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab={t.statsTabHourly} key="hourly">
          <Card>
            <Select
              placeholder={t.statsHourlySelectDate}
              style={{ width: 200, marginBottom: 16 }}
              onChange={(date) => loadHourlyStats(date)}
            >
              {(summary?.daily_activity || []).map(day => (
                <Option key={day.date} value={day.date}>
                  {day.date}
                </Option>
              ))}
            </Select>
          </Card>

          <Card title={t.statsHourlyTitle} style={{ marginTop: 16 }}>
            <Table
              columns={hourlyColumns}
              dataSource={hourlyStats}
              rowKey={(record) => `${record.date}-${record.hour}-${record.event_type}`}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab={t.statsTabPopular} key="services">
          <Card title={t.statsPopularServicesTitle}>
            <Table
              columns={[
                { title: t.statsColService, dataIndex: 'service_name', key: 'service_name' },
                { title: t.statsColCount, dataIndex: 'count', key: 'count' },
                { title: t.statsColLang, dataIndex: 'language', key: 'language' },
              ]}
              dataSource={summary?.popular_services || []}
              rowKey={(record) => `${record.service_id}-${record.language}`}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab={t.statsTabLangs} key="languages">
          <Card title={t.statsLangDistributionTitle}>
            <Table
              columns={[
                { title: t.statsColLang, dataIndex: 'language', key: 'language' },
                { title: t.statsColCount, dataIndex: 'count', key: 'count' },
                { title: t.statsColPercent, dataIndex: 'percent', key: 'percent', render: (value: number) => `${value.toFixed(1)}%` },
              ]}
              dataSource={summary?.language_distribution || []}
              rowKey="language"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>
      </Tabs>
      </div>
    </AdminLayout>
  );
}
